/**
 * @file: The rules for the values in the interval panel.
 *
 * The state manager owns the panel. The baseline is the stored row as last
 * read. For a new interval, the baseline is the values that the panel opened
 * with. The draft is what the panel shows and the operator edits. An edit
 * gives a new draft and never changes the old one.
 *
 * A draft of a new interval can have no role. The panel then shows the role
 * that the typed times derive, and that role is not an edit.
 *
 * The functions are pure, so the specs need no calendar and no panel.
 */
import moment from 'moment'
import { intervalTimesProblem } from '../../../shared/interval-times'
import { combineDateAndTime, dayOffset, endDayIsChosen, overnightEndDate } from 'common/date-time'

/** An empty field and an id of either type compare as one value. */
const idOf = value => value === null || value === undefined || value === '' ? null : Number(value)

/** The time of a date field and a time field as a number, or null. */
const timeOf = (datePart, timePart) => {
  const joined = combineDateAndTime(datePart, timePart)

  return joined ? joined.valueOf() : null
}

const withFields = (draft, fields) => Object.freeze(Object.assign({}, draft, fields))

/**
 * The end day of a draft as a count of days from the start day, and whether
 * the operator chose it. The count stays while the start date is empty, so
 * the next start date can move the end by the same count.
 */
function endDayOf (draft) {
  return Object.freeze({ offset: dayOffset(draft), chosen: endDayIsChosen(draft) })
}

/** A new start date moves the end date by the same number of days. The clock times stay. */
function moveEndWithStart ({ draft, endDay }) {
  if (!draft.startDate || endDay.offset === null) {
    return { draft, endDay }
  }

  return { draft: withFields(draft, { endDate: moment(draft.startDate).add(endDay.offset, 'days').toDate() }), endDay }
}

/** A typed end date is the choice of the operator, unless the midnight rule gives the same day. */
function rememberEndDay ({ draft }) {
  return { draft, endDay: endDayOf(draft) }
}

/** An end time before the start time ends on the next day, unless the operator chose the end day. */
function applyMidnightRule ({ draft, endDay }) {
  if (!draft.startDate || !draft.startTime || !draft.endTime) {
    return { draft, endDay }
  }

  const ruled = endDay.chosen ? draft : withFields(draft, { endDate: overnightEndDate(draft) })

  return { draft: ruled, endDay: Object.freeze({ offset: dayOffset(ruled), chosen: endDay.chosen }) }
}

/** The rule that runs after an edit of each field. Other fields need no rule. */
const FIELD_RULES = Object.freeze({
  startDate: moveEndWithStart,
  endDate: rememberEndDay,
  startTime: applyMidnightRule,
  endTime: applyMidnightRule
})

/**
 * Give the draft and the end-day memory after an edit.
 *
 * @param {Object} state - { draft, endDay }
 * @param {Object} changes - The fields that the operator changed, with their new values
 * @returns {Object} A new { draft, endDay }
 */
function withEdit ({ draft, endDay }, changes) {
  const edited = { draft: withFields(draft, changes), endDay }

  return Object.keys(changes)
    .filter(field => FIELD_RULES[field])
    .reduce((state, field) => FIELD_RULES[field](state), edited)
}

/**
 * Give the draft after a drag or a resize of the bar. The drag saved the
 * times, so the draft takes them. The other edits of the operator stay.
 *
 * @param {Object} draft - The draft before the drag
 * @param {Object} row - The stored row after the drag
 * @returns {Object} A new { draft, endDay }
 */
function withSavedTimes (draft, { startDate, startTime, endDate, endTime, updatedAt }) {
  const followed = withFields(draft, { startDate, startTime, endDate, endTime, updatedAt })

  return { draft: followed, endDay: endDayOf(followed) }
}

/** A draft without a role follows the typed times, and that is not an edit. */
const roleIsEdited = (baseline, draft) => Boolean(draft.role) && draft.role !== (baseline.role || null)

/**
 * True when a save writes something that the baseline does not have. The
 * fields are the playlist, the role, the times and a request for the stretch.
 *
 * The times are compared as moments, so a time field that reports the same
 * time again is not a change.
 */
function hasUnsavedEdits (baseline, draft) {
  if (!baseline || !draft) {
    return false
  }

  return idOf(draft.sceneCollectionId) !== idOf(baseline.sceneCollectionId) ||
    roleIsEdited(baseline, draft) ||
    timeOf(draft.startDate, draft.startTime) !== timeOf(baseline.startDate, baseline.startTime) ||
    timeOf(draft.endDate, draft.endTime) !== timeOf(baseline.endDate, baseline.endTime) ||
    draft.winsPeers === true
}

/**
 * Give the reason that a save of the draft cannot work, or null.
 *
 * The panel disables Save and shows this reason. The save path reads the same
 * rule, so a click never gives a reason that the panel did not show. The rule
 * for the times is the rule of the server.
 *
 * A new interval can have no role. The save then sends none, and the server
 * derives the role.
 *
 * @param {Object} draft - The draft of the panel
 * @param {Object} standing - The standing of the draft, with its role
 * @returns {string|null} A sentence for the operator
 */
function saveProblem (draft, { role, refusal }) {
  if (!idOf(draft.sceneCollectionId)) return 'Select a playlist.'
  if (!role && draft.intervalId) return 'Select a role.'

  const start = combineDateAndTime(draft.startDate, draft.startTime)
  const end = combineDateAndTime(draft.endDate, draft.endTime)

  if (!start) return 'Fill in the start time.'
  if (!end) return 'Fill in the end time.'

  // The server refuses the same save, for example a role change into a full
  // band. The panel gives the reason before the click.
  return intervalTimesProblem(start, end) || refusal || null
}

export { endDayOf, withEdit, withSavedTimes, hasUnsavedEdits, saveProblem }
